import { toBlob } from "html-to-image";

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024;
const MAX_LONG_EDGE_PX = 1600;
const MIN_PIXEL_RATIO = 0.35;
export const FEEDBACK_SCREENSHOT_EXCLUDE_ATTR = "data-feedback-screenshot-exclude";

export async function captureAppScreenshot(element: HTMLElement): Promise<string | null> {
  const { width, height } = measureElement(element);
  if (width <= 0 || height <= 0) return null;

  const initialRatio = Math.min(1, MAX_LONG_EDGE_PX / Math.max(width, height));
  const ratios = uniqueRatios([
    initialRatio,
    initialRatio * 0.75,
    initialRatio * 0.5,
    Math.min(initialRatio, MIN_PIXEL_RATIO),
  ]);

  for (const pixelRatio of ratios) {
    const blob = await toBlob(element, {
      backgroundColor: "#fff",
      cacheBust: false,
      pixelRatio,
      filter: shouldCaptureNode,
    });
    if (!blob || blob.size === 0) continue;
    if (blob.size > MAX_SCREENSHOT_BYTES) continue;
    return blobToDataUrl(blob);
  }

  return null;
}

function measureElement(element: HTMLElement): { width: number; height: number } {
  const rect = element.getBoundingClientRect();
  return {
    width: Math.ceil(rect.width || element.offsetWidth || 0),
    height: Math.ceil(rect.height || element.offsetHeight || 0),
  };
}

function shouldCaptureNode(node: Node): boolean {
  if (!(node instanceof Element)) return true;
  return !node.closest(`[${FEEDBACK_SCREENSHOT_EXCLUDE_ATTR}="true"]`);
}

function uniqueRatios(ratios: number[]): number[] {
  return [...new Set(ratios.map((ratio) => Number(ratio.toFixed(3))))]
    .filter((ratio) => ratio > 0)
    .sort((a, b) => b - a);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("Failed to read screenshot"));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read screenshot"));
    reader.readAsDataURL(blob);
  });
}
